import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { ScrollArea } from './ui/scroll-area';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';
import { toast } from 'sonner';
import {
  Sparkles, RefreshCw, ChevronDown, ChevronRight, Lightbulb,
  AlertTriangle, TrendingUp, Plus, Loader2, Brain, Target,
  CheckCircle2, BarChart3, Users, DollarSign, Ticket, FileText
} from 'lucide-react';
import api from '../api';

// Priority colors
const PRIORITY_COLORS = {
  high: 'bg-red-100 text-red-700 border-red-200',
  medium: 'bg-amber-100 text-amber-700 border-amber-200',
  low: 'bg-green-100 text-green-700 border-green-200',
};

// Category icons
const CATEGORY_ICONS = {
  sales: DollarSign,
  marketing: Target,
  support: Ticket,
  operations: BarChart3,
};

// Insight level badges
const LEVEL_CONFIG = {
  executive: { color: 'bg-purple-100 text-purple-700', label: 'Executive View' },
  manager: { color: 'bg-blue-100 text-blue-700', label: 'Manager View' },
  staff: { color: 'bg-green-100 text-green-700', label: 'Personal View' },
};

export default function AIInsightsPanel() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [insights, setInsights] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    insights: true,
    tasks: true,
    alerts: false,
    opportunities: false,
  });
  const [creatingTask, setCreatingTask] = useState(null);

  const fetchInsights = async (showRefreshIndicator = false) => {
    try {
      if (showRefreshIndicator) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      const response = await api.get('/leadership-ai/insights');
      setInsights(response.data);
    } catch (error) {
      console.error('Failed to fetch AI insights:', error);
      toast.error('Failed to load AI insights');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchInsights();
  }, []);

  const handleRefresh = () => {
    fetchInsights(true);
  };

  const handleCreateTask = async (task, triggerRefresh = null) => {
    try {
      setCreatingTask(task.title);
      const params = new URLSearchParams({
        title: task.title,
        description: task.description,
        priority: task.priority || 'medium',
        category: task.category || 'operations'
      });
      
      // Add deliverable_type if present
      if (task.deliverable_type) {
        params.append('deliverable_type', task.deliverable_type);
      }
      
      const response = await api.post(`/leadership-ai/create-suggested-task?${params.toString()}`);
      toast.success(`Task "${task.title}" created successfully! Check the task list below.`);
      
      // Refresh insights to show updated task count
      fetchInsights(true);
      
      // Trigger parent component refresh if callback provided
      if (typeof triggerRefresh === 'function') {
        triggerRefresh();
      }
      
      // Dispatch custom event to notify other components
      window.dispatchEvent(new CustomEvent('leadership-task-created', { 
        detail: { taskId: response.data?.task_id, task: response.data?.task } 
      }));
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create task');
    } finally {
      setCreatingTask(null);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  if (loading) {
    return (
      <Card className="border-violet-200">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Skeleton className="h-5 w-5 rounded-full" />
            <Skeleton className="h-5 w-32" />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!insights) {
    return (
      <Card className="border-violet-200">
        <CardContent className="py-8 text-center">
          <Brain className="h-12 w-12 mx-auto mb-3 text-violet-300" />
          <p className="text-muted-foreground">Unable to load AI insights</p>
          <Button onClick={handleRefresh} variant="outline" className="mt-3">
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const { insight_level, ai_insights, company_data } = insights;
  const levelConfig = LEVEL_CONFIG[insight_level] || LEVEL_CONFIG.staff;

  return (
    <Card className="border-violet-200" data-testid="ai-insights-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                AI Insights
                <Badge variant="outline" className={levelConfig.color}>
                  {levelConfig.label}
                </Badge>
              </CardTitle>
              <CardDescription>
                Personalized recommendations based on company data
              </CardDescription>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Key Insights */}
        <Collapsible open={expandedSections.insights} onOpenChange={() => toggleSection('insights')}>
          <CollapsibleTrigger className="w-full">
            <div className="flex items-center justify-between p-3 bg-violet-50 dark:bg-violet-950/20 rounded-lg hover:bg-violet-100 dark:hover:bg-violet-950/30 transition-colors">
              <div className="flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-violet-600" />
                <span className="font-medium">Key Insights</span>
                <Badge variant="outline" className="text-xs">
                  {ai_insights.insights?.length || 0}
                </Badge>
              </div>
              {expandedSections.insights ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="mt-2 space-y-2">
              {ai_insights.insights?.map((insight, idx) => (
                <div key={idx} className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg border-l-4 border-violet-400">
                  <p className="text-sm">{insight}</p>
                </div>
              ))}
              {(!ai_insights.insights || ai_insights.insights.length === 0) && (
                <p className="text-sm text-muted-foreground p-3">No insights available</p>
              )}
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* Suggested Tasks */}
        <Collapsible open={expandedSections.tasks} onOpenChange={() => toggleSection('tasks')}>
          <CollapsibleTrigger className="w-full">
            <div className="flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-950/30 transition-colors">
              <div className="flex items-center gap-2">
                <Target className="h-5 w-5 text-blue-600" />
                <span className="font-medium">Suggested AI Tasks</span>
                <Badge variant="outline" className="text-xs">
                  {ai_insights.suggested_tasks?.length || 0}
                </Badge>
              </div>
              {expandedSections.tasks ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="mt-2 space-y-2">
              {ai_insights.suggested_tasks?.map((task, idx) => {
                const CategoryIcon = CATEGORY_ICONS[task.category] || FileText;
                const isCreating = creatingTask === task.title;
                
                // Deliverable type display
                const deliverableLabels = {
                  'research_report': { label: 'Research Report', color: 'bg-purple-100 text-purple-700' },
                  'spreadsheet': { label: 'Excel/Data', color: 'bg-green-100 text-green-700' },
                  'document': { label: 'Document', color: 'bg-blue-100 text-blue-700' },
                  'copy': { label: 'Copy/Content', color: 'bg-amber-100 text-amber-700' },
                  'presentation': { label: 'PowerPoint', color: 'bg-orange-100 text-orange-700' },
                };
                const deliverableInfo = deliverableLabels[task.deliverable_type] || deliverableLabels['document'];
                
                return (
                  <div key={idx} className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg border-l-4 border-blue-400">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-2 flex-1">
                        <CategoryIcon className="h-4 w-4 mt-0.5 text-muted-foreground" />
                        <div className="flex-1">
                          <h4 className="font-medium text-sm">{task.title}</h4>
                          <p className="text-xs text-muted-foreground mt-1">{task.description}</p>
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            <Badge variant="outline" className={PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.medium}>
                              {task.priority}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {task.category}
                            </Badge>
                            <Badge variant="outline" className={`text-xs ${deliverableInfo.color}`}>
                              📄 {deliverableInfo.label}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="default"
                        className="h-8 text-xs flex-shrink-0 bg-blue-600 hover:bg-blue-700"
                        onClick={() => handleCreateTask(task)}
                        disabled={isCreating}
                        data-testid={`create-task-${idx}`}
                      >
                        {isCreating ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <>
                            <Plus className="h-3 w-3 mr-1" />
                            Create Task
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                );
              })}
              {(!ai_insights.suggested_tasks || ai_insights.suggested_tasks.length === 0) && (
                <p className="text-sm text-muted-foreground p-3">No task suggestions available</p>
              )}
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* Alerts */}
        {ai_insights.alerts && ai_insights.alerts.length > 0 && (
          <Collapsible open={expandedSections.alerts} onOpenChange={() => toggleSection('alerts')}>
            <CollapsibleTrigger className="w-full">
              <div className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-950/20 rounded-lg hover:bg-red-100 dark:hover:bg-red-950/30 transition-colors">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                  <span className="font-medium">Alerts</span>
                  <Badge variant="outline" className="bg-red-100 text-red-700 text-xs">
                    {ai_insights.alerts.length}
                  </Badge>
                </div>
                {expandedSections.alerts ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </div>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="mt-2 space-y-2">
                {ai_insights.alerts.map((alert, idx) => (
                  <div key={idx} className="p-3 bg-red-50 dark:bg-red-950/20 rounded-lg border-l-4 border-red-400">
                    <p className="text-sm">{alert}</p>
                  </div>
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Opportunities */}
        {ai_insights.opportunities && ai_insights.opportunities.length > 0 && (
          <Collapsible open={expandedSections.opportunities} onOpenChange={() => toggleSection('opportunities')}>
            <CollapsibleTrigger className="w-full">
              <div className="flex items-center justify-between p-3 bg-emerald-50 dark:bg-emerald-950/20 rounded-lg hover:bg-emerald-100 dark:hover:bg-emerald-950/30 transition-colors">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-emerald-600" />
                  <span className="font-medium">Opportunities</span>
                  <Badge variant="outline" className="bg-emerald-100 text-emerald-700 text-xs">
                    {ai_insights.opportunities.length}
                  </Badge>
                </div>
                {expandedSections.opportunities ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </div>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="mt-2 space-y-2">
                {ai_insights.opportunities.map((opportunity, idx) => (
                  <div key={idx} className="p-3 bg-emerald-50 dark:bg-emerald-950/20 rounded-lg border-l-4 border-emerald-400">
                    <p className="text-sm">{opportunity}</p>
                  </div>
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Quick Stats based on role */}
        {company_data && (
          <div className="pt-3 border-t">
            <p className="text-xs text-muted-foreground mb-2">Data Snapshot</p>
            <div className="grid grid-cols-3 gap-2">
              {company_data.sales && (
                <div className="text-center p-2 bg-slate-50 dark:bg-slate-900 rounded">
                  <p className="text-lg font-bold text-blue-600">{company_data.sales.open_deals}</p>
                  <p className="text-xs text-muted-foreground">Open Deals</p>
                </div>
              )}
              {company_data.support && (
                <div className="text-center p-2 bg-slate-50 dark:bg-slate-900 rounded">
                  <p className="text-lg font-bold text-amber-600">{company_data.support.open_tickets}</p>
                  <p className="text-xs text-muted-foreground">Open Tickets</p>
                </div>
              )}
              {company_data.total_overdue_tasks !== undefined && (
                <div className="text-center p-2 bg-slate-50 dark:bg-slate-900 rounded">
                  <p className="text-lg font-bold text-red-600">{company_data.total_overdue_tasks}</p>
                  <p className="text-xs text-muted-foreground">Overdue Tasks</p>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
